<template>
  <div
    class="min-h-screen flex items-center justify-center bg-background py-12 px-4 sm:px-6 lg:px-8"
  >
    <div class="max-w-md w-full space-y-8 bg-card rounded-xl border border-border shadow-sm p-8">
      <div>
        <h2 class="mt-6 text-center text-3xl font-extrabold text-foreground">
          {{ $t('auth.chooseUsername') }}
        </h2>
        <p class="mt-2 text-center text-sm text-muted-foreground">
          {{ $t('auth.pickUniqueUsername') }}
        </p>
      </div>

      <form class="mt-8 space-y-6" novalidate @submit.prevent="handleSubmit">
        <AlertBanner v-if="error" variant="error">
          <p class="text-sm">{{ error }}</p>
        </AlertBanner>

        <div>
          <label for="username" class="block text-sm font-medium text-foreground mb-1">{{
            $t('auth.usernameLabel')
          }}</label>
          <input
            id="username"
            v-model="username"
            type="text"
            :class="[
              'appearance-none relative block w-full px-3 py-2 border placeholder-muted-foreground text-foreground rounded-md focus:outline-none sm:text-sm',
              fieldError
                ? 'border-destructive focus:ring-destructive focus:border-destructive'
                : 'border-input bg-input-background focus:ring-ring focus:border-ring',
            ]"
            :placeholder="$t('auth.usernamePlaceholder')"
          />
          <p v-if="fieldError" class="mt-1 text-sm text-destructive">{{ fieldError }}</p>
        </div>

        <TermsConsentCheckbox v-if="needsConsent" v-model="termsAccepted" :error="termsError" />

        <div>
          <button
            type="submit"
            :disabled="submitting || (needsConsent && !termsAccepted)"
            class="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-primary-foreground bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <LoadingSpinner :loading="submitting" />
            {{ $t('auth.saveUsername') }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import apiClient from '../../api/client'
import LoadingSpinner from '../../components/LoadingSpinner.vue'
import AlertBanner from '../../components/AlertBanner.vue'
import TermsConsentCheckbox from '../../components/TermsConsentCheckbox.vue'
import { useAuthStore } from '../../stores/auth'
import { useLocalePath } from '../../composables/useLocalePath'

const router = useRouter()
const { t } = useI18n()
const authStore = useAuthStore()
const { localePath } = useLocalePath()

const username = ref('')
const termsAccepted = ref(false)
const submitting = ref(false)
const error = ref(null)
const fieldError = ref(null)
const termsError = ref(null)

const needsConsent = computed(() => !authStore.user?.terms_accepted_at)

onMounted(async () => {
  if (!authStore.token) {
    // CompleteUsername is a locale-shared route (no `.en` twin), so it can't
    // be passed through `localePath` itself — but the Login target it redirects
    // to can and must be.
    router.push({ ...localePath('Login'), query: { redirect: '/complete-username' } })
    return
  }

  if (authStore.user?.username) {
    router.push(localePath('Home'))
    return
  }

  try {
    const response = await apiClient.get('/users/username_suggestion')
    if (response.data.suggestion) {
      username.value = response.data.suggestion
    }
  } catch {
    // Suggestion is optional — user can still type their own
  }
})

watch(username, () => {
  fieldError.value = null
})
watch(termsAccepted, () => {
  termsError.value = null
})

const handleSubmit = async () => {
  error.value = null
  fieldError.value = null
  termsError.value = null

  if (!username.value.trim()) {
    fieldError.value = t('auth.usernameRequired')
    return
  }

  if (needsConsent.value && !termsAccepted.value) {
    termsError.value = t('auth.termsRequired')
    return
  }

  submitting.value = true

  try {
    const payload = { username: username.value }
    if (needsConsent.value) {
      payload.accept_terms = termsAccepted.value
    }
    const response = await apiClient.patch('/users/me', { user: payload })
    authStore.user = response.data
    router.push(localePath('Home'))
  } catch (error_) {
    const data = error_.response?.data
    if (data?.errors && Array.isArray(data.errors)) {
      const hasUsernameError = data.errors.some((msg) => msg.startsWith('Username'))
      const hasTermsError = data.errors.some((msg) => msg.startsWith('Terms'))
      if (hasUsernameError) {
        fieldError.value = t('auth.usernameAlreadyTaken')
      } else if (hasTermsError) {
        termsError.value = t('auth.termsRequired')
      } else {
        error.value = t('common.somethingWentWrong')
      }
    } else {
      error.value = t('auth.failedToSaveUsername')
    }
  } finally {
    submitting.value = false
  }
}
</script>
